import { DecisionMakingSummary } from "openapi/api/model/decisionMakingSummary";
import type { Candidate } from "../components/types";


  
export  type DecisionSummaryCardProps = {
    summary: DecisionMakingSummary
    decision: string
    reasoning: string
    candidate?: Candidate
  }
  


export function generateDetailedExplanation(
    decision: string,
    summary: DecisionMakingSummary,
    mainSkills:string[]
) {

    // Determine main skills from the candidate's experience
    console.log({summary})

    // Calculate role match score (simplified version)
    const roleMatchScore = summary.roleMatch ? 0.85 : 0.2;

    // Create job requirements object
    const jobRequirements = {
        mainSkills: mainSkills,
    };

    // Generate the explanation
    const explanation = getCandidateExplanation(
        decision,
        summary,
        jobRequirements,
        roleMatchScore
    );

    // Convert markdown-style bold to HTML
    const formattedExplanation = explanation.replace(
        /\*\*(.*?)\*\*/g,
        "<strong>$1</strong>"
    );

    return (
        <div
            className="text-sm leading-relaxed"
            dangerouslySetInnerHTML={{ __html: formattedExplanation }}
        />
    );
}

export function getCandidateExplanation(
  classification: string,
  summary: DecisionMakingSummary,
  jobRequirements: { mainSkills: string[] },
  roleMatchScore: number
): string {
  const mainSkill = jobRequirements.mainSkills[0];
  const years = summary.total_experience_years.toFixed(1);
  const mainSkillYears = summary.mainTechSpecialtyExperience;
  const roleScore = roleMatchScore.toFixed(1);
  const niceToHaveScore = summary.niceToHave_skill_score.toFixed(2);
  const problemSpaceScore = summary.totalProblemSpaceScore.toFixed(2);
  const isSenior = summary.total_experience_years >= 5;

  function format(text: string): string {
    return text
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>") // Markdown bold
      .replace(/\n/g, "<br>"); // Line breaks
  }

  switch (classification) {
    case "Super Candidate":
      return format(`This candidate is classified as a **Super Candidate** based on outstanding alignment across all dimensions:
- ✅ Experience requirement met with **${years} years**
- ✅ Deep expertise in **${mainSkill}** (**${mainSkillYears} years** as a core skill)
- ✅ Strong role alignment (role match score: **${roleScore}**)
- ✅ Excellent additive/cultural skills (nice-to-have score: **${niceToHaveScore}**)

These combined signals suggest exceptional fit and high impact potential.`);

    case "Top Candidate":
      return format(`This candidate is classified as a **Top Candidate**, demonstrating strong technical and experiential alignment:
- ✅ Senior-level experience (**${years} years**)
- ✅ Core technical fit in **${mainSkill}** (**${mainSkillYears} years**)
- ✅ Role relevance (role match score: **${roleScore}**)
- ✅ Acceptable additive/cultural skills (nice-to-have score: **${niceToHaveScore}**)

While not as well-rounded as a "Super Candidate" in nice-to-have areas, their seniority and hard-skill match make them a high-value prospect.`);

    case "Full Evaluation":
      return format(`This candidate is flagged for **Full Evaluation**, showing technical promise despite limited seniority:
- ⚠️ Junior-level experience (**${years} years**)
- ✅ Technical specialty in **${mainSkill}** confirmed
- ✅ Role relevance detected (role match score: **${roleScore}**)
- ✅ Problem space familiarity score: **${problemSpaceScore}**

They meet technical and contextual thresholds, but need further evaluation due to limited experience.`);

    case "No Match":
    default: {
      const failsTech = summary.match_tech_specialty === false;
      const failsSeniorDomain = isSenior && summary.totalProblemSpaceScore < 0.6;
      const failsJuniorDomain = !isSenior && summary.totalProblemSpaceScore < 0.4;
      const failsRole=!summary.roleMatch
      const failsNiceToHaves=summary.niceToHave_skill_score < 0.4

      console.log('reason: ',{failsJuniorDomain,failsSeniorDomain,failsTech})

      const reasons: string[] = [];

      if (failsTech)
        reasons.push(`❌ missing required experience in **${mainSkill}** (core tech not confirmed)`);
      if (failsSeniorDomain)
        reasons.push(`❌ senior-level candidate lacks sufficient domain familiarity (problem space score: **${problemSpaceScore}**, threshold: 0.6)`);
      if (failsJuniorDomain)
        reasons.push(`❌ junior candidate lacks interest or exposure to the domain (problem space score: **${problemSpaceScore}**, threshold: 0.4)`);
      if (failsRole)
        reasons.push(`❌ The candidate has minimal experience in roles similar to this one (role match score: **${roleScore}**)`);
      if (failsNiceToHaves)
        reasons.push(`❌ The candidate has scored too low in the nice-to-have skills (nice-to-have skills score: **${niceToHaveScore}**)`);

      return format(`This candidate is marked as a **No Match** due to the following:
${reasons.map(r => `- ${r}`).join("\n")}`);
    }
  }
}




export const formatScore = (score: number) => {
    return Math.round(score * 100);
};

export const formatExperience = (years: number) => {
    return years.toFixed(1);
};

export const generateFailedPrequaliReason = (reasoning: string): string => {
    if (reasoning.includes('Missmatched statuses')) {
      const matches = reasoning.match(/required:\s*(\{.*?\})\s*received:\s*(\{.*?\})/s);
      if (matches && matches.length >= 3) {
        try {
          const required = JSON.parse(matches[1]);
          const received = JSON.parse(matches[2]);
  
          const expectedStatuses = Object.entries(required.acceptedStatuses || {})
            .map(([country, statuses]) => `${country}: ${(statuses as string[]).join(', ')}`)
            .join('; ');
  
          const receivedStatus = received.acceptedStatuses;
  
          return `This resume failed pre-qualification because the candidate's work authorization does not match the job requirements. The job requires: ${expectedStatuses}. The candidate has: ${receivedStatus}.`;
        } catch (err) {
          return `This resume failed pre-qualification due to mismatched work authorization. We could not parse the specific details.`;
        }
      }
  
      return `This resume failed pre-qualification due to mismatched work authorization.`;
    }
  
    if (reasoning.includes('Mismatched locations')) {
      const matches = reasoning.match(/required:\s*([^\n]+)\s*@\s*([^\n]+)\s*received:\s*"([^"]+)"/s);
      if (matches && matches.length >= 4) {
        const locationFlexibility = matches[1].trim();
        const requiredLocation = matches[2].trim();
        const receivedLocation = matches[3].trim();
  
        return `This resume failed pre-qualification because the candidate's location does not meet the job's requirements. The job requires someone ${locationFlexibility.toLowerCase()} in ${requiredLocation}, but the candidate is located in ${receivedLocation}.`;
      }
  
      return `This resume failed pre-qualification due to location mismatch.`;
    }
  
    return `This resume failed pre-qualification because it does not meet one or more critical job requirements. Reason: **${reasoning}**`;
  };
  